/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// react
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link,
  useParams,
  useNavigate,
  useLocation,
  useOutletContext,
  Outlet
} from 'react-router-dom';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectReviews } from '../../reviews/store/reviewsSlice';
// actions
import useInstance from '../../../actions/useInstance';
import { getInstance } from '../../../actions';
import { getReviews } from '../../reviews/store/reviewsSlice';
// BASE components
import BASEPageSimple from '../../../../@BASE/core/BASEPageSimple';
import BASELoading from '../../../../@BASE/core/BASELoading';
import BASESvgIcon from '../../../../@BASE/core/BASESvgIcon';
import useThemeMediaQuery from '../../../../@BASE/hooks/useThemeMediaQuery';
import NavLinkAdapter from '../../../../@BASE/core/NavLinkAdapter';
import { updateNavigationItem } from '../../../store/BASE/navigationSlice';
// material-ui
import { styled, useTheme, lighten } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
// custom components
import PageHeader from './views/PageHeader';
import ContentView from './views/ContentView';
import PageInfo from './PageInfo';
import SectionPageList from './SectionPageList';
import NavigationTabination from '../../../shared-components/sub-navigation/NavigationTabination';

const Root = styled(BASEPageSimple)(({ theme }) => ({
  '& .BASEPageSimple-header': {},
  '& .BASEPageSimple-toolbar': {},
  '& .BASEPageSimple-content': {},
  '& .page-header': {
    backgroundColor: theme.palette.background.royalBlue,
    color: theme.palette.getContrastText(theme.palette.primary.main)
  },
  '& .BASEPageSimple-sidebarHeader': {},
  '& .BASEPageSimple-sidebarContent': {}
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& td, & th': {
    border: 'solid 1px #000',
    backgroundColor: 'transparent'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

function PageView(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const theme = useTheme();
  const routeParams = useParams();
  const navigate = useNavigate();
  // instance hook
  const {
    instance,
    instanceDetails,
    handleInstanceAction,
    loading,
    error,
    setError
  } = useInstance('pages', routeParams.id, 'view');
  const [section, setSection] = useState(null);
  const [reviews, setReviews] = useState([]);

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(!isMobile);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  const [value, setValue] = React.useState(0);

  const [mockupImgElement, setMockupImgElement] = useState(null);
  const mockupImgRef = useCallback((node) => {
    if (node !== null) {
      setMockupImgElement(node);
    }
  }, []);

  const [mockupImg, setMockupImg] = useState({
    width: 0,
    height: 0
  });
  const [lastKnownImgSize, setLastKnownImgSize] = useState({
    width: 0,
    height: 0
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // get data from API on page load
  const getAllPages = async () => {
    try {
      const response = await dispatch(
        getInstance('sections')(instance?.section?.id)
      ).unwrap();
      setSection(response);
      setReviews(response?.lesson?.reviews);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (instance) {
      getAllPages();
      dispatch(
        updateNavigationItem('course-component', {
          url: `courses/${instance?.section?.lesson?.course?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('lesson-component', {
          url: `lessons/${instance?.section?.lesson?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('section-component', {
          url: `sections/${instance?.section?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('page-component', {
          url: `pages/${routeParams.id}`,
          disabled: false
        })
      );
    }
  }, [dispatch, instance]);

  useEffect(() => {
    if (location.pathname.includes('/comments')) {
      setRightSidebarOpen(true);
      setLeftSidebarOpen(false);
    } else {
      setRightSidebarOpen(false);
      setLeftSidebarOpen(!isMobile);
    }
  }, [isMobile, location.pathname]);

  const debounce = (callback, delay = 200) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  };

  const debouncedResize = useCallback(
    debounce((width, height) => {
      setMockupImg((prev) => {
        if (prev.width !== width || prev.height !== height) {
          return { width, height };
        }
        return prev;
      });
    }, 300)
  );

  useEffect(() => {
    if (!mockupImgElement) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries.length) return;

      const { width, height } = entries[0].contentRect;

      debouncedResize(width, height);
    });

    observer.observe(mockupImgElement);
    setMockupImg({
      width: mockupImgElement.clientWidth,
      height: mockupImgElement.clientHeight
    });

    return () => observer.disconnect();
  }, [mockupImgElement]);

  useEffect(() => {
    setLastKnownImgSize(mockupImg);
  }, [value]);

  if (!instance) {
    return <BASELoading />;
  }

  return (
    <Root
      content={
        <div className="w-full">
          <div className="page-header w-full">
            <PageHeader
              instance={instance}
              details={instanceDetails}
              resource="pages"
              action="view"
              onSave={null}
              save_disabled
              leftSidebarToggle={(ev) => {
                setLeftSidebarOpen(!leftSidebarOpen);
              }}
            />
          </div>
          <Hidden lgUp>
            <Paper
              className="flex sticky top-0 z-10 items-center w-full px-16 py-8 border-b-1 shadow-0"
              square
            >
              <IconButton
                to={`/sections/${instance?.section?.id}`}
                component={Link}
                className=""
              >
                <BASESvgIcon>
                  {theme.direction === 'ltr'
                    ? 'heroicons-outline:arrow-sm-left'
                    : 'heroicons-outline:arrow-sm-right'}
                </BASESvgIcon>
              </IconButton>

              <Typography className="text-13 font-medium tracking-tight mx-10">
                Back to section
              </Typography>
            </Paper>
          </Hidden>

          <div className="flex justify-center w-full">
            <Paper className="w-full rounded-0 shadow overflow-hidden">
              <div
                className="prose prose-sm dark:prose-invert w-full max-w-full"
                dir={theme.direction}
              >
                <div className="w-full">
                  <div className="flex flex-col">
                    <Box className="py-16" sx={{ bgcolor: '#555555' }}>
                      <div
                        className={`container ${
                          value === 0 ? 'visible' : 'hidden'
                        }`}
                        style={{
                          height: '100%',
                          width: '100%'
                        }}
                      >
                        <img
                          ref={mockupImgRef}
                          className="max-w-[100%] my-0 mx-auto shadow-md w-3/4"
                          src={`https://base-media-repo.web.app/templates/${instance?.template?.label}/mockups/Desktop.jpg`}
                          alt={`${instance?.template?.title}} Mockup`}
                        />
                      </div>
                      <div
                        className={`container ${
                          value === 1 ? 'visible' : 'hidden'
                        }`}
                        style={{
                          height: '100%'
                        }}
                      >
                        {section?.navigation &&
                          section?.navigation?.length > 0 && (
                            <NavigationTabination
                              pages={section.pages}
                              navigation={section.navigation}
                              section={section}
                              currentPageId={instance.id}
                              isActive={value === 1}
                              parentIsActive={value === 1}
                              imgSize={lastKnownImgSize}
                            />
                          )}
                      </div>
                    </Box>
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      aria-label="change between example and subnavigation"
                    >
                      <Tab
                        label="Example Image"
                        id="example_image_tab"
                        aria-controls="example-image-tab"
                      />
                      {section?.navigation &&
                        section?.navigation?.length > 0 && (
                          <Tab
                            label="Subnavigation"
                            id="Subnavigation_tab"
                            aria-controls="Subnavigation-tab"
                          />
                        )}
                    </Tabs>

                    {instance.content && (
                      <div className="container px-16">
                        <ContentView
                          obj={instance.content}
                          label={instance?.template?.label}
                          rtl={instance?.section?.lesson?.course?.language?.rtl}
                          ins={instance?.template?.instructions}
                          course={instance?.section?.lesson?.course}
                          comments={instance?.comments}
                        />
                      </div>
                    )}
                  </div>

                  <Divider className="mt-24 mb-0" />

                  <div className="flex flex-col p-16">
                    <Accordion
                      className="bg-transparent shadow-none border-0"
                      sx={{
                        [`&:before`]: {
                          display: 'none'
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        className="px-0 border-0"
                      >
                        <Typography
                          variant="subtitle2"
                          className="font-semibold"
                        >
                          Branching:
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="px-0">
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: instance?.template?.branching
                          }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <Divider className="m-0" />

                  <div className="flex flex-col p-16">
                    <Accordion
                      className="bg-transparent shadow-none border-0"
                      sx={{
                        [`&:before`]: {
                          display: 'none'
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        className="px-0 border-0"
                      >
                        <Typography
                          variant="subtitle2"
                          className="font-semibold"
                        >
                          Programming Notes:
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="px-0">
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: instance?.template?.dev_notes
                          }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <Divider className="m-0" />

                  <div className="flex flex-col p-16">
                    <Accordion
                      className="bg-transparent shadow-none border-0"
                      sx={{
                        [`&:before`]: {
                          display: 'none'
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        className="px-0 border-0"
                      >
                        <Typography
                          variant="subtitle2"
                          className="font-semibold"
                        >
                          Graphic Artist Notes:
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="px-0">
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: instance?.template?.ga_notes
                          }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <Divider className="m-0" />

                  <div className="flex flex-col p-16">
                    <Accordion
                      className="bg-transparent shadow-none border-0"
                      sx={{
                        [`&:before`]: {
                          display: 'none'
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        className="px-0 border-0"
                      >
                        <Typography
                          variant="subtitle2"
                          className="font-semibold"
                        >
                          508 Compliance Plan:
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="px-0">
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: instance?.template?.accessibility
                          }}
                        />
                        {instance?.template?.access_table && (
                          <TableContainer
                            component={Paper}
                            className="rounded-none mt-20 bg-transparent shadow-none"
                          >
                            <Table
                              sx={{ minWidth: 650 }}
                              aria-label="simple table"
                            >
                              <TableHead>
                                <TableRow>
                                  <StyledTableCell>Element</StyledTableCell>
                                  <StyledTableCell>
                                    Form of Compliance
                                  </StyledTableCell>
                                  <StyledTableCell>Notes</StyledTableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {instance?.template?.access_table.map(
                                  (row, rowIndex) =>
                                    row.items.map((item, itemIndex) => (
                                      <StyledTableRow
                                        key={`${rowIndex}-${itemIndex}`}
                                      >
                                        {itemIndex === 0 && (
                                          <StyledTableCell
                                            rowSpan={row.items.length}
                                            component="th"
                                            scope="row"
                                          >
                                            {row.element}
                                          </StyledTableCell>
                                        )}
                                        <StyledTableCell>
                                          {item.compliance}
                                        </StyledTableCell>
                                        <StyledTableCell
                                          dangerouslySetInnerHTML={{
                                            __html: item.notes
                                          }}
                                        />
                                      </StyledTableRow>
                                    ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </div>

                  <Divider className="m-0" />

                  <div className="flex flex-col p-16">
                    <Accordion
                      className="bg-transparent shadow-none border-0"
                      sx={{
                        [`&:before`]: {
                          display: 'none'
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                        className="px-0 border-0"
                      >
                        <Typography
                          variant="subtitle2"
                          className="font-semibold"
                        >
                          Additional Information:
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="px-0">
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: instance?.template?.info
                          }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </div>
                </div>
              </div>
            </Paper>
          </div>
        </div>
      }
      leftSidebarOpen={leftSidebarOpen}
      leftSidebarOnClose={() => {
        setLeftSidebarOpen(false);
      }}
      leftSidebarWidth={288}
      leftSidebarContent={
        <>
          <div className="p-32">
            <Button
              to={`/sections/${instance?.section.id}`}
              component={Link}
              className="mb-24"
              color="secondary"
              variant="text"
              startIcon={
                <BASESvgIcon size={20}>
                  {theme.direction === 'ltr'
                    ? 'heroicons-outline:arrow-sm-left'
                    : 'heroicons-outline:arrow-sm-right'}
                </BASESvgIcon>
              }
            >
              Back to Section
            </Button>

            <PageInfo section={section} />
          </div>
          <Divider />
          <SectionPageList section={section} />
        </>
      }
      rightSidebarContent={
        <div
          className={
            isMobile ? 'relative' : 'fixed top-0 h-screen overflow-y-auto'
          }
          style={isMobile ? { width: '100%' } : { width: 384 }}
        >
          <Outlet context={{ instance, reviews }} />
        </div>
      }
      rightSidebarOpen={rightSidebarOpen}
      rightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={384}
      scroll={isMobile ? 'normal' : 'content'}
    />
  );
}

export default PageView;
